import React, { useEffect, useMemo, useState } from "react";
import Breadcrumb from "../../components/common/Breadcrumb";
import { getCompanys, GetDetailedPFData } from "../../services/dataService";
import Loading from "../../components/common/Loading";
import ReactDataTable from "react-data-table-component";
import { IconFilter } from "@tabler/icons-react";
import FilterComponent from "../pages/FilterComponent";
import jsPDF from "jspdf";
import "jspdf-autotable";
import toast from "react-hot-toast";
import { getSecureItem } from "../../services/SecureStorage";

function DetailedPfReport() {
  const UID = getSecureItem("userId");
  const [loading, setLoading] = useState(false);
  const [pfData, setPfData] = useState([]);
  const [filterText, setFilterText] = React.useState("");
  const [datafilter, setDateFilter] = useState([]);
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [companyDetails, setCompanyDetails] = useState([]);
  const [loadingInitialy, setLoadingInitialy] = useState(true);
  const [resetPaginationToggle, setResetPaginationToggle] =
    React.useState(false);
  const subHeaderComponentMemo = useMemo(() => {}, [
    filterText,
    resetPaginationToggle,
  ]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = {
        UserId: UID,
      };

      const response = await GetDetailedPFData(data);
      console.log("PF Data Response:", response);
      setPfData(response.data.data);
    } catch (error) {
      console.error("Error fetching PF data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyDetails = async () => {
    try {
      const response = await getCompanys(UID);
      setCompanyDetails(response.data.data[0]);
    } catch (error) {
      console.error("An error occurred:", error);
      toast.error("An error occurred while fetching company details");
    }
  };

  const columnsFilter = [
    {
      name: "Sl No",
      cell: (row, index) => {
        const firstRow = filteredItems.indexOf(row);
        return firstRow + 1;
      },
      grow: 0,
      width: "60px",
    },
    {
      name: "Employee",
      selector: (row) => row.Name,
      sortable: true,
    },
    {
      name: "Code",
      selector: (row) => row.Code,
      sortable: true,
    },
    {
      name: "Opening Date",
      selector: (row) => {
        if (!row.PfOpeningDate) return "";
        const date = new Date(row.PfOpeningDate);
        return (
          String(date.getDate()).padStart(2, "0") +
          "-" +
          String(date.getMonth() + 1).padStart(2, "0") +
          "-" +
          date.getFullYear()
        );
      },
      sortable: true,
    },
    {
      name: "Opening Amount",
      selector: (row) => row.OpeningAmount,
      sortable: true,
    },
    {
      name: "TotalPFAmount",
      selector: (row) => row.TotalPFAmount,
      sortable: true,
    },
    {
      name: "Total",
      selector: (row) =>
        Number(row.TotalPFAmount) + Number(row.OpeningAmount || 0),
      sortable: true,
    },
  ];

  const columnsFilterForFilter = [
    {
      name: "Employee",
      selector: (row) => row.Name,
      sortable: true,
    },
    {
      name: "Code",
      selector: (row) => row.Code,
      sortable: true,
    },
    {
      name: "Profitcenter",
      selector: (row) => row.ProfitcenterName,
      sortable: true,
    },
    {
      name: "Department",
      selector: (row) => row.DepartmentName,
      sortable: true,
    },
    {
      name: "Category",
      selector: (row) => row.CategoryName,
      sortable: true,
    },
    {
      name: "Opening Date",
      selector: (row) => {
        if (!row.PfOpeningDate) return "";
        const date = new Date(row.PfOpeningDate);
        return (
          String(date.getDate()).padStart(2, "0") +
          "-" +
          String(date.getMonth() + 1).padStart(2, "0") +
          "-" +
          date.getFullYear()
        );
      },
      sortable: true,
    },
    {
      name: "Opening Amount",
      selector: (row) => row.OpeningAmount,
      sortable: true,
    },
  ];

  const filteredItems = datafilter.filter((el) => {
    const search = filterText.toLowerCase();
    return (
      el.Name?.toLowerCase().includes(search) ||
      String(el.Code)?.toLowerCase().includes(search) ||
      el.CategoryName?.toLowerCase().includes(search) ||
      el.ProfitcenterName?.toLowerCase().includes(search)
    );
  });

  const handleFilterClick = () => {
    setIsFilterVisible(!isFilterVisible);
  };

  const handleFilter = (filteredResults) => {
    setDateFilter(filteredResults);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return (
      String(date.getDate()).padStart(2, "0") +
      "-" +
      String(date.getMonth() + 1).padStart(2, "0") +
      "-" +
      date.getFullYear()
    );
  };

  const handleDownloadPdf = (filteredItems) => {
    const doc = new jsPDF();

    // Set title
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(`${companyDetails?.Name || "Company Name"}`, 105, 20, {
      align: "center",
    });

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text("Detailed PF Report", 105, 30, { align: "center" });

    // Group data by ProfitcenterName
    const groupedData = filteredItems.reduce((acc, item) => {
      const profitCenter = item.ProfitcenterName || "Other";
      if (!acc[profitCenter]) {
        acc[profitCenter] = [];
      }
      acc[profitCenter].push(item);
      return acc;
    }, {});

    let yPosition = 40;
    let serialNumber = 1;

    // Iterate through each profit center group
    Object.keys(groupedData).forEach((profitCenter, groupIndex) => {
      const groupData = groupedData[profitCenter];

      // Add space between groups
      if (groupIndex > 0) {
        yPosition += 8;
      }

      // Check if new page is needed
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
      }

      // Add group header
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(`Profit Center: ${profitCenter}`, 15, yPosition);
      yPosition += 8;

      // Prepare table data
      const tableData = groupData.map((row) => [
        serialNumber++,
        row.Name || "",
        row.Code || "",
        formatDate(row.PfOpeningDate),
        Number(row.OpeningAmount || 0).toFixed(2),
        Number(row.TotalPFAmount || 0).toFixed(2),
        (
          Number(row.OpeningAmount || 0) + Number(row.TotalPFAmount || 0)
        ).toFixed(2),
      ]);

      // Calculate group totals
      const groupTotalOpening = groupData.reduce(
        (sum, row) => sum + Number(row.OpeningAmount || 0),
        0
      );
      const groupTotalPF = groupData.reduce(
        (sum, row) => sum + Number(row.TotalPFAmount || 0),
        0
      );
      const groupGrandTotal = groupTotalOpening + groupTotalPF;

      // Add group total row
      tableData.push([
        "",
        "Group Total",
        "",
        "",
        groupTotalOpening.toFixed(2),
        groupTotalPF.toFixed(2),
        groupGrandTotal.toFixed(2),
      ]);

      // Create table with borders and black header background
      doc.autoTable({
        startY: yPosition,
        head: [
          [
            "Sl No",
            "Employee",
            "Code",
            "Opening Date",
            "Opening Amt",
            "Total PF Amt",
            "Total",
          ],
        ],
        body: tableData,
        theme: "grid",
        headStyles: {
          fillColor: [0, 0, 0], // Black background for header
          textColor: [255, 255, 255], // White text for header
          fontStyle: "bold",
          fontSize: 10,
          halign: "center",
        },
        bodyStyles: {
          fontSize: 9,
        },
        columnStyles: {
          0: { halign: "center", cellWidth: 15 },
          1: { cellWidth: 40 },
          2: { halign: "center", cellWidth: 20 },
          3: { halign: "center", cellWidth: 25 },
          4: { halign: "right", cellWidth: 25 },
          5: { halign: "right", cellWidth: 25 },
          6: { halign: "right", cellWidth: 25 },
        },
        didParseCell: function (data) {
          // Style group total row
          if (data.row.index === tableData.length - 1) {
            data.cell.styles.fontStyle = "bold";
          }
        },
        margin: { left: 15, right: 15 },
      });

      yPosition = doc.lastAutoTable.finalY + 5;
    });

    // Calculate and add grand totals
    const grandTotalOpening = filteredItems.reduce(
      (sum, row) => sum + Number(row.OpeningAmount || 0),
      0
    );
    const grandTotalPF = filteredItems.reduce(
      (sum, row) => sum + Number(row.TotalPFAmount || 0),
      0
    );
    const grandTotal = grandTotalOpening + grandTotalPF;

    // Check if new page is needed
    if (yPosition > 270) {
      doc.addPage();
      yPosition = 20;
    }

    // Add grand total section
    yPosition += 8;
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Grand Total Summary", 15, yPosition);

    doc.autoTable({
      startY: yPosition + 5,
      head: [
        ["Description", "Opening Amount", "Total PF Amount", "Grand Total"],
      ],
      body: [
        [
          "All Profit Centers",
          grandTotalOpening.toFixed(2),
          grandTotalPF.toFixed(2),
          grandTotal.toFixed(2),
        ],
      ],
      theme: "grid",
      headStyles: {
        fillColor: [0, 0, 0], // Black background for header
        textColor: [255, 255, 255], // White text for header
        fontStyle: "bold",
        fontSize: 10,
      },
      bodyStyles: {
        fontStyle: "bold",
        fontSize: 9,
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { halign: "right", cellWidth: 40 },
        2: { halign: "right", cellWidth: 40 },
        3: { halign: "right", cellWidth: 40 },
      },
      margin: { left: 15, right: 15 },
    });

    // Add footer with generation date
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Generated on: ${new Date().toLocaleString()} | Page ${i} of ${pageCount}`,
        15,
        doc.internal.pageSize.getHeight() - 10
      );
    }

    // Save the PDF
    doc.save(
      `Detailed_PF_Report_${new Date().toISOString().split("T")[0]}.pdf`
    );
  };
  useEffect(() => {
    fetchData();
    fetchCompanyDetails();
  }, [UID]);

  useEffect(() => {
    setDateFilter(pfData);
  }, [pfData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoadingInitialy(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        {/* Left Side - Breadcrumb */}
        <Breadcrumb title={"Detailed Pf Report"} />

        {/* Right Side - Filter, Search & PDF */}
        <div className="flex items-center gap-2">
          <button
            className="bg-sky rounded-[4px] px-3 py-2 flex items-center justify-center"
            onClick={handleFilterClick}
          >
            <IconFilter className="text-white w-[18px] h-[18px]" />
          </button>

          <input
            type="search"
            placeholder="Search by Name, Code"
            className="border w-[350px] rounded px-2 py-1"
            onChange={(e) => setFilterText(e.target.value)}
          />

          <button
            onClick={() => handleDownloadPdf(filteredItems)}
            className="btn btn-danger"
          >
            PDF
          </button>
        </div>
      </div>

      {isFilterVisible && (
        <FilterComponent
          columns={columnsFilterForFilter}
          data={pfData}
          onFilter={handleFilter}
        />
      )}

      <div className="react-data-table">
        <div className="react-data-table">
          {loadingInitialy ? (
            <div className="flex flex-col items-center">
              {/* Spinner */}
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-lg font-medium text-gray-700">
                Loading...
              </p>
            </div>
          ) : (
            <ReactDataTable
              columns={columnsFilter}
              data={filteredItems}
              pagination
              paginationResetDefaultPage={resetPaginationToggle}
              subHeader
              subHeaderComponent={subHeaderComponentMemo}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default DetailedPfReport;
